import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { Modal, ModalFooter } from '@/components/ui/Modal';
import Button from '@/components/ui/Button';
import Input from '@/components/ui/Input';
import { Table, Column } from '@/components/ui/Table';
import { uploadService } from '@/services/upload.service';
import { UploadResponse } from '@/types/upload';
import api from '@/utils/api';
import {
    Loader2,
    Check,
    Search,
    ChevronLeft,
    ChevronRight,
    Link as LinkIcon
} from 'lucide-react';

interface CreateLeadCapturePageModalProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    userId?: string;
}

// Simple debounce hook for stable search
function useDebounce<T>(value: T, delay: number): T {
    const [debouncedValue, setDebouncedValue] = useState<T>(value);
    useEffect(() => {
        const handler = setTimeout(() => setDebouncedValue(value), delay);
        return () => clearTimeout(handler);
    }, [value, delay]);
    return debouncedValue;
}

export const CreateLeadCapturePageModal: FC<CreateLeadCapturePageModalProps> = ({
    open,
    onOpenChange,
    userId,
}) => {
    // Phase control: 'config' vs 'success'
    const [isSuccess, setIsSuccess] = useState(false);

    // Core State
    const [title, setTitle] = useState('');
    const [isAllDocuments, setIsAllDocuments] = useState(true);
    const [selectedDocIds, setSelectedDocIds] = useState<string[]>([]);

    // Data State (Paginated & Searchable)
    const [documents, setDocuments] = useState<UploadResponse[]>([]);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(1);
    const [searchQuery, setSearchQuery] = useState('');
    const debouncedSearch = useDebounce(searchQuery, 350);
    const pageSize = 7; // Optimized for fixed height

    // UI Feedback
    const [loading, setLoading] = useState(false);
    const [creating, setCreating] = useState(false);
    const [generatedLink, setGeneratedLink] = useState<string | null>(null);
    const [copied, setCopied] = useState(false);

    // Optimized Fetch
    const fetchDocuments = useCallback(async () => {
        setLoading(true);
        try {
            const res = await uploadService.getAllDocuments(pageSize, (page - 1) * pageSize, debouncedSearch);
            setDocuments(res.data || []);
            setTotal(res.total || 0);
        } catch (error) {
            console.error('[CaptureModal] Fetch error:', error);
        } finally {
            setLoading(false);
        }
    }, [page, debouncedSearch, pageSize]);

    // Data Sync
    useEffect(() => {
        if (open && !isAllDocuments && !isSuccess) {
            fetchDocuments();
        }
    }, [open, isAllDocuments, fetchDocuments, isSuccess]);

    // Reset pagination on search
    useEffect(() => { setPage(1); }, [debouncedSearch]);

    const handleCreate = async () => {
        if (!title.trim()) return;
        setCreating(true);
        try {
            const res = await api.post('/mxm/leads/pages', {
                title: title.trim(),
                isAllDocuments,
                documentIds: isAllDocuments ? [] : selectedDocIds,
                userId: userId,
            });
            const link = `${window.location.origin}/documents/${res.data.id}`;
            setGeneratedLink(link);
            setIsSuccess(true);
        } catch (error) {
            console.error('[CaptureModal] Create error:', error);
        } finally {
            setCreating(false);
        }
    };

    const handleCopy = () => {
        if (generatedLink) {
            navigator.clipboard.writeText(generatedLink);
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        }
    };

    const handleClose = () => {
        onOpenChange(false);
        // Reset after animation
        setTimeout(() => {
            setTitle('');
            setIsAllDocuments(true);
            setSelectedDocIds([]);
            setGeneratedLink(null);
            setIsSuccess(false);
            setPage(1);
            setSearchQuery('');
        }, 200);
    };

    const toggleSelectDoc = (id: string) => {
        setSelectedDocIds(prev => prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]);
    };

    const totalPages = Math.ceil(total / pageSize);

    // High-Density Column Definition
    const columns: Column<UploadResponse>[] = useMemo(() => [
        {
            key: 'title',
            header: 'Document Name',
            render: (doc) => {
                const docId = doc.mxmUpload?.id || doc.id;
                const active = selectedDocIds.includes(docId);
                return (
                    <div
                        className="flex items-center gap-3 py-1 cursor-pointer group"
                        onClick={(e) => { e.stopPropagation(); toggleSelectDoc(docId); }}
                    >
                        <div className={`w-3.5 h-3.5 rounded border transition-all flex items-center justify-center shrink-0 ${active ? 'bg-blue-600 border-blue-600' : 'bg-white border-slate-300 group-hover:border-blue-400'}`}>
                            {active && <Check className="w-2.5 h-2.5 text-white stroke-[3.5]" />}
                        </div>
                        <span className={`text-xs font-medium truncate ${active ? 'text-slate-900' : 'text-slate-600 grayscale group-hover:grayscale-0 transition-all'}`}>
                            {doc.title || doc.mxmUpload?.title || doc.filename || 'Untitled'}
                        </span>
                    </div>
                );
            }
        },
        {
            key: 'date',
            header: 'Uploaded',
            className: 'text-right w-24',
            render: (doc) => (
                <span className="text-[10px] text-slate-400 tabular-nums">
                    {new Date(doc.createdAt || doc.mxmUpload?.createdAt || Date.now()).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                </span>
            )
        }
    ], [selectedDocIds]);

    return (
        <Modal
            isOpen={open}
            onClose={handleClose}
            title="Create Lead Capture Page"
            size="md"
        >
            <div className="h-[480px] flex flex-col pt-2 antique-body">
                {isSuccess ? (
                    <div className="flex-1 flex flex-col items-center justify-center animate-in fade-in duration-300 px-8 text-center">
                        <div className="w-12 h-12 bg-green-50 rounded-full flex items-center justify-center mb-4">
                            <Check className="w-6 h-6 text-green-600" />
                        </div>
                        <h3 className="text-base font-bold text-slate-900 mb-1">Capture Page Ready</h3>
                        <p className="text-xs text-slate-500 mb-8">Anyone with this link can access these documents after providing their email.</p>

                        <div className="w-full flex items-center gap-2 p-1.5 pl-4 bg-slate-50 border border-slate-200 rounded-lg">
                            <LinkIcon className="w-3 h-3 text-slate-400 shrink-0" />
                            <span className="flex-1 text-[11px] font-mono text-slate-600 truncate text-left">{generatedLink}</span>
                            <Button size="sm" onClick={handleCopy} className="h-8 px-4 text-[11px] font-bold">
                                {copied ? 'Copied' : 'Copy Link'}
                            </Button>
                        </div>
                    </div>
                ) : (
                    <div className="flex-1 flex flex-col min-h-0 space-y-6">
                        {/* Compact Field Settings */}
                        <div className="grid grid-cols-2 gap-4 shrink-0">
                            <div className="space-y-1.5">
                                <label className="text-[10px] font-bold text-slate-500 uppercase tracking-wider">Page Title</label>
                                <Input
                                    placeholder="Enter title..."
                                    value={title}
                                    onChange={(e) => setTitle(e.target.value)}
                                    className="h-9 text-xs border-slate-200 bg-white"
                                />
                            </div>
                            <div className="space-y-1.5">
                                <label className="text-[10px] font-bold text-slate-500 uppercase tracking-wider">Document Select</label>
                                <div
                                    onClick={() => setIsAllDocuments(!isAllDocuments)}
                                    className={`h-9 px-3 rounded-md border flex items-center justify-between cursor-pointer transition-all ${isAllDocuments ? 'bg-slate-900 border-slate-900' : 'bg-white border-slate-200'}`}
                                >
                                    <span className={`text-xs font-medium ${isAllDocuments ? 'text-white' : 'text-slate-600'}`}>
                                        {isAllDocuments ? 'All Documents' : 'Manual Selection'}
                                    </span>
                                    <div className={`w-6 h-3.5 rounded-full p-0.5 transition-colors ${isAllDocuments ? 'bg-blue-600' : 'bg-slate-200'}`}>
                                        <div className={`w-2.5 h-2.5 rounded-full bg-white transition-transform ${isAllDocuments ? 'translate-x-2.5' : 'translate-x-0'}`} />
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Inventory Table Area - Hidden when All Documents is active */}
                        {!isAllDocuments && (
                            <div className="flex-1 flex flex-col min-h-0 animate-in fade-in duration-200">
                                <div className="flex items-center justify-between gap-4 mb-2">
                                    <label className="text-[10px] font-bold text-slate-500 uppercase tracking-wider">
                                        Documents ({selectedDocIds.length} selected)
                                    </label>
                                    <div className="relative w-40">
                                        <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400" />
                                        <input
                                            placeholder="Search..."
                                            value={searchQuery}
                                            onChange={(e) => setSearchQuery(e.target.value)}
                                            className="w-full h-8 pl-8 pr-3 text-[11px] border border-slate-200 rounded-md focus:outline-none focus:border-blue-500 bg-white transition-all shadow-sm"
                                        />
                                    </div>
                                </div>

                                <div className="flex-1 border border-slate-200 rounded-lg overflow-hidden bg-white flex flex-col shadow-sm relative">
                                    {loading && (
                                        <div className="absolute inset-0 bg-white/60 backdrop-blur-[1px] z-10 flex items-center justify-center transition-all">
                                            <Loader2 className="w-5 h-5 animate-spin text-blue-600" />
                                        </div>
                                    )}
                                    <div className="flex-1 overflow-y-auto min-h-0">
                                        <Table
                                            data={documents}
                                            columns={columns}
                                            emptyMessage="No documents found."
                                            variant="default"
                                            className="border-none"
                                        />
                                    </div>

                                    {totalPages > 1 && (
                                        <div className="h-10 px-4 bg-slate-50 border-t border-slate-100 flex items-center justify-between shrink-0">
                                            <span className="text-[10px] font-bold text-slate-500">Page {page} / {totalPages}</span>
                                            <div className="flex items-center gap-1">
                                                <button
                                                    disabled={page === 1}
                                                    onClick={() => setPage(p => p - 1)}
                                                    className="w-7 h-7 flex items-center justify-center border border-slate-200 bg-white rounded hover:bg-slate-50 disabled:opacity-30 transition-all"
                                                >
                                                    <ChevronLeft className="w-3.5 h-3.5 text-slate-600" />
                                                </button>
                                                <button
                                                    disabled={page === totalPages}
                                                    onClick={() => setPage(p => p + 1)}
                                                    className="w-7 h-7 flex items-center justify-center border border-slate-200 bg-white rounded hover:bg-slate-50 disabled:opacity-30 transition-all"
                                                >
                                                    <ChevronRight className="w-3.5 h-3.5 text-slate-600" />
                                                </button>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}

                        {isAllDocuments && (
                            <div className="flex-1 flex flex-col items-center justify-center border border-dashed border-slate-200 rounded-lg bg-slate-50/50 animate-in fade-in duration-300">
                                <div className="p-3 bg-white rounded-full shadow-sm mb-3">
                                    <LinkIcon className="w-5 h-5 text-blue-500" />
                                </div>
                                <h4 className="text-xs font-bold text-slate-900 uppercase tracking-widest text-center px-4">Full User Documents</h4>
                                <p className="text-[11px] text-slate-500 mt-1 max-w-[280px] text-center">
                                    This link will automatically include every document.
                                </p>
                            </div>
                        )}
                    </div>
                )}
            </div>

            <ModalFooter className="border-t pt-4">
                {isSuccess ? (
                    <Button onClick={handleClose} variant="secondary" className="w-full h-10 font-bold">Done</Button>
                ) : (
                    <div className="flex items-center justify-end gap-3 w-full">
                        <Button variant="ghost" onClick={handleClose} className="h-10 text-slate-400 font-bold">Cancel</Button>
                        <Button
                            onClick={handleCreate}
                            disabled={creating || !title.trim() || (!isAllDocuments && selectedDocIds.length === 0)}
                            className="h-10 px-6 font-bold min-w-[120px]"
                        >
                            {creating ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Create Link'}
                        </Button>
                    </div>
                )}
            </ModalFooter>
        </Modal>
    );
};
